import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bot,
  Copy,
  Eye,
  MoreVertical,
  Play,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { canEditOwn, useAuthStore } from "@/features/auth/store";
import { useGuardedAction } from "@/features/auth/access";
import {
  useAgents,
  useDeleteAgent,
  useDeployAgent,
  useDuplicateAgent,
} from "@/features/agents/hooks";
import type { Agent } from "@/features/agents/api";
import { cn } from "@/lib/utils";

const STATUS_STYLES: Record<string, string> = {
  active:
    "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400",
  draft:
    "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
  paused:
    "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400",
  inactive:
    "text-rose-600 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-400",
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] ?? "text-muted-foreground bg-muted";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
        cls,
      )}
    >
      {status}
    </span>
  );
}

export default function AgentsPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role ?? "member");
  // Customers own everything they can see here, so rank does not apply.
  const canEdit = canEditOwn(role);
  const guard = useGuardedAction();

  const q = useAgents();
  const agents = q.data ?? [];

  const deleteM = useDeleteAgent();
  const deployM = useDeployAgent();
  const duplicateM = useDuplicateAgent();

  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null);

  async function onDeploy(id: string) {
    try {
      await deployM.mutateAsync(id);
      toast.success("Agent deployed");
    } catch {
      toast.error("Failed to deploy agent");
    }
  }

  async function onDuplicate(id: string) {
    try {
      await duplicateM.mutateAsync(id);
      toast.success("Agent duplicated");
    } catch {
      toast.error("Failed to duplicate agent");
    }
  }

  async function onConfirmDelete() {
    if (!deleteTarget) return;
    try {
      await deleteM.mutateAsync(deleteTarget.id);
      toast.success("Agent deleted");
      setDeleteTarget(null);
    } catch {
      toast.error("Failed to delete agent");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Agents</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your AI voice agents.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => guard(() => navigate("/agents/create"))}>
            <Plus className="size-4" />
            Create Agent
          </Button>
        )}
      </div>

      {q.isLoading && (
        <Card>
          <CardContent className="space-y-2 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </CardContent>
        </Card>
      )}

      {q.isError && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-destructive">
            Failed to load agents.
          </CardContent>
        </Card>
      )}

      {!q.isLoading && agents.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <Bot className="size-10 text-muted-foreground" />
            <div>
              <p className="text-base font-medium">No agents yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Create your first AI voice agent to get started.
              </p>
            </div>
            {canEdit && (
              <Button onClick={() => guard(() => navigate("/agents/create"))}>
                <Plus className="size-4" />
                Create Agent
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!q.isLoading && agents.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Agent</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Calls</th>
                    <th className="px-4 py-3 font-medium">Minutes</th>
                    <th className="px-4 py-3 font-medium">Language</th>
                    <th className="w-12 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {agents.map((agent) => (
                    <tr
                      key={agent.id}
                      className="cursor-pointer hover:bg-accent/40"
                      onClick={() => navigate(`/agents/${agent.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400">
                            <Bot className="size-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium">{agent.name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {agent.description || "No description"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={agent.status} />
                      </td>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground">
                        {(agent.total_calls ?? 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground">
                        {(agent.total_minutes ?? 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {agent.language || "—"}
                      </td>
                      <td
                        className="px-4 py-3 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8"
                              aria-label={`Actions for ${agent.name}`}
                            >
                              <MoreVertical className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/agents/${agent.id}`)}
                            >
                              <Eye className="size-4" />
                              View
                            </DropdownMenuItem>
                            {canEdit && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => onDeploy(agent.id)}
                                  disabled={deployM.isPending}
                                >
                                  <Play className="size-4" />
                                  Deploy
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => onDuplicate(agent.id)}
                                  disabled={duplicateM.isPending}
                                >
                                  <Copy className="size-4" />
                                  Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setDeleteTarget(agent)}
                                >
                                  <Trash2 className="size-4" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete agent</AlertDialogTitle>
            <AlertDialogDescription>
              Delete{" "}
              <span className="font-medium text-foreground">
                {deleteTarget?.name}
              </span>
              ? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteM.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteM.isPending}
              onClick={(e) => {
                e.preventDefault();
                onConfirmDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteM.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
